import { CountryCode } from '@dayetopia/library';
import { FirebaseError } from 'firebase/app';
import { RecaptchaVerifier, Auth } from 'firebase/auth';
import { RECAPTCHA_ID, RECAPTCHA_ID_ENROLLMENT, RECAPTCHA_ID_LOGIN } from '@constants';

// Global initialization state tracking
const initializationState: { [key: string]: boolean } = {};

// Helper function to clean up any existing reCAPTCHA instances
const cleanupRecaptcha = (containerId?: string) => {
  // Clear any existing verifier
  if (window.recaptchaVerifier) {
    try {
      window.recaptchaVerifier.clear();
    } catch (error) {
      // Silent cleanup - errors are expected during cleanup
    }
    window.recaptchaVerifier = undefined;
  }

  // Clear widget ID
  if (window.recaptchaWidgetId) {
    window.recaptchaWidgetId = undefined;
  }

  // Clean up reCAPTCHA elements from the specified container or all containers
  const containerIds = containerId ? [containerId] : [RECAPTCHA_ID, RECAPTCHA_ID_ENROLLMENT, RECAPTCHA_ID_LOGIN];

  containerIds.forEach((id) => {
    const existingContainer = document.getElementById(id);
    if (existingContainer) {
      // Remove all child elements that might be reCAPTCHA related
      const children = Array.from(existingContainer.children);
      children.forEach((child) => {
        if (child.tagName === 'DIV' || child.tagName === 'IFRAME' || child.tagName === 'TEXTAREA') {
          child.remove();
        }
      });

      existingContainer.innerHTML = '';
      existingContainer.style.cssText = '';
    }
  });

  // Clean up global reCAPTCHA elements
  document.querySelectorAll('.grecaptcha-badge').forEach((badge) => badge.remove());

  // Reset reCAPTCHA state
  if ((window as any).grecaptcha && (window as any).grecaptcha.reset) {
    try {
      (window as any).grecaptcha.reset();
    } catch (error) {
      // Silent cleanup - errors are expected during cleanup
    }
  }
};

// Wait for Firebase Auth to be ready
const waitForFirebaseAuthReady = async (firebaseAuth: Auth, timeout = 15000): Promise<void> => {
  return new Promise((resolve, reject) => {
    const startTime = Date.now();

    const checkAuthReady = () => {
      try {
        if (firebaseAuth && firebaseAuth.app && firebaseAuth.config && firebaseAuth.app.options && firebaseAuth.app.options.projectId) {
          if (firebaseAuth.currentUser !== undefined || firebaseAuth.currentUser === null) {
            resolve();
            return;
          }
        }
      } catch (error) {
        // If accessing properties throws, Auth isn't ready yet
      }

      if (Date.now() - startTime >= timeout) {
        reject(new Error(`Firebase Auth initialization timeout after ${timeout}ms`));
        return;
      }

      setTimeout(checkAuthReady, 300);
    };

    checkAuthReady();
  });
};

// Check if reCAPTCHA testing is disabled
const isRecaptchaTestingDisabled = (): boolean => {
  return process.env.DISABLE_RECAPTCHA === 'true';
};

// Simplified Firebase Auth reCAPTCHA setup - let Firebase handle everything
export const setInvisibleRecaptchaOnWindow = async (
  firebaseAuth: Auth,
  onVerificationCallback?: () => void,
  containerId: string = RECAPTCHA_ID,
  forceInvisible: boolean = false
) => {
  try {
    // Check if already initializing for this container
    if (initializationState[containerId]) {
      return;
    }

    // Check if a functional recaptcha verifier already exists
    if (window.recaptchaVerifier) {
      try {
        // Verify the existing recaptcha is still valid and functional
        if (window.recaptchaVerifier.render && typeof window.recaptchaVerifier.render === 'function') {
          return; // Reuse existing functional verifier
        }
      } catch (error) {
        // If the existing verifier is broken, we'll create a new one
        window.recaptchaVerifier = undefined;
      }
    }

    // Mark as initializing
    initializationState[containerId] = true;

    // Clean up any existing instances first
    cleanupRecaptcha(containerId);

    // Wait for Firebase Auth to be fully initialized
    await waitForFirebaseAuthReady(firebaseAuth);

    // Check if reCAPTCHA testing is disabled
    if (isRecaptchaTestingDisabled()) {
      // For test mode, immediately call verification callback if provided
      if (onVerificationCallback) {
        onVerificationCallback();
      }
      delete initializationState[containerId];
      return;
    }

    // Enhanced RecaptchaVerifier configuration for mobile compatibility
    const ua = navigator.userAgent;
    const isMobileSafari =
      /(?!.*CriOS)(iPhone|iPad).*Safari/.test(ua) ||
      (/Macintosh.*Safari/.test(ua) && !/Chrome/.test(ua) && !/CriOS/.test(ua) && navigator.maxTouchPoints > 0);
    const useVisibleForMobileSafari = isMobileSafari && containerId === RECAPTCHA_ID_ENROLLMENT;
    let size: 'normal' | 'invisible';
    if (useVisibleForMobileSafari) size = 'normal';
    else if (forceInvisible || containerId !== RECAPTCHA_ID_LOGIN) size = 'invisible';
    else size = 'normal';
    const recaptchaConfig = {
      size,
      callback: () => {
        if (onVerificationCallback) {
          onVerificationCallback();
        }
      },
      'error-callback': () => {
        delete initializationState[containerId];
      }
    };

    // RUNTIME VALIDATION AND LOGGING
    if (!firebaseAuth || typeof firebaseAuth !== 'object') {
      throw new Error('[reCAPTCHA] Invalid firebaseAuth instance');
    }
    if (typeof containerId === 'string') {
      const el = document.getElementById(containerId);
      if (!el) {
        throw new Error(`[reCAPTCHA] Container element with id "${containerId}" not found in DOM`);
      }
    } else {
      throw new Error('[reCAPTCHA] containerId must be a string (element id)');
    }
    if (!recaptchaConfig || typeof recaptchaConfig !== 'object') {
      throw new Error('[reCAPTCHA] recaptchaConfig must be an object');
    }

    window.recaptchaVerifier = new RecaptchaVerifier(firebaseAuth, containerId, recaptchaConfig);
    await window.recaptchaVerifier.render();

    // Mark as initialized
    delete initializationState[containerId];
  } catch (error) {
    // Clear initialization state on error
    delete initializationState[containerId];
    const errorMessage = (error as Error).message;
    throw new Error(`reCAPTCHA setup failed: ${errorMessage}`);
  }
};

// Enhanced cleanup function for use in components — also clears the init lock
// so a subsequent mount can re-initialize (unlike internal cleanupRecaptcha which
// preserves the lock during the init flow to prevent the render race condition).
export const cleanupRecaptchaVerifier = (containerId?: string) => {
  cleanupRecaptcha(containerId);

  const containerIds = containerId ? [containerId] : [RECAPTCHA_ID, RECAPTCHA_ID_ENROLLMENT, RECAPTCHA_ID_LOGIN];
  containerIds.forEach((id) => {
    delete initializationState[id];
  });
};

// FirebaseError type guard
export const isFirebaseError = (error: any): error is FirebaseError => {
  return error && typeof error === 'object' && 'code' in error && typeof error.code === 'string';
};

/**
 * Set Firebase Auth language code based on the user's country.
 * This ensures Firebase emails (verification, password reset) are sent in the correct language.
 *
 * @param auth - Firebase Auth instance
 * @param country - User's country from CountryCode enum
 */
export const setFirebaseLanguageFromCountry = (auth: Auth | null | undefined, country: CountryCode): void => {
  if (!auth) return;

  // Only Bulgarian gets localized emails, all other countries get English
  // eslint-disable-next-line no-param-reassign
  auth.languageCode = country === CountryCode.BG ? 'bg' : 'en';
};
